import dayjs from 'dayjs';
import { Role, UserIdentityProvider } from 'graphql/generated/graphqlTypes';
import { generateMockMobileDevice } from './mobileDevice';
import { generateMockVisitor } from './visitor';
import {
  mockVisitorGroupAlpha,
  mockVisitorGroupBeta,
  mockVisitorGroupCardHolders,
  mockVisitorGroupCleaners,
  mockVisitorGroupCoContractors,
  mockVisitorGroupCognitoTeam,
  mockVisitorGroupDelta,
  mockVisitorGroupEntraTeam,
  mockVisitorGroupEpsilon,
  mockVisitorGroupEta,
  mockVisitorGroupGamma,
  mockVisitorGroupInstallers,
  mockVisitorGroupInternalTeam,
  mockVisitorGroupIota,
  mockVisitorGroupKappa,
  mockVisitorGroupKeySharingUsers,
  mockVisitorGroupLambda,
  mockVisitorGroupMu,
  mockVisitorGroupNu,
  mockVisitorGroupOmicron,
  mockVisitorGroupTheta,
  mockVisitorGroupXi,
  mockVisitorGroupZeta
} from './visitorGroupFixtures';
import { generateMockVisitorGroupMembership } from './visitorGroupMembership';

// These fixtures back the automated tests for the Users page (sorting, filtering, searching).
// The visitor IDs are referenced by name in ~13 other mock files (card assignments, access events,
// the dashboard "clean state" list and the per-visitor details mocks in mocks/admin/queries.ts),
// so IDs must stay stable — only the data hanging off them is shaped for testing.
//
// Only the 8 visitors in `mockVisitorsWithGroups` (bottom of this file) reach the Users overview
// table. Everything else exported here still backs detail pages, cards, access events and the
// dashboard, so the per-visitor notes below describe the fixture itself — read the summary above
// `mockVisitorsWithGroups` for what the table actually shows.
//
// Three details decide what the Users page shows, and each is easy to get wrong:
//   - status  is a number 0-5 from getStatusPriority, not a label. Reaching 0/1/2 requires a
//             REGISTERED `ACCESS` device; an unregistered or missing one forces 3 or 4, and an
//             unregistered `CONFIG` device outranks everything at 5.
//   - expiration column reads getUserDeletionDate (the LATEST membership expirationDate, or null
//             when any membership is permanent), while the `expires` FILTER reads the deprecated
//             visitor-level `expirationDate`. They are different fields — both are populated below.
//   - cards   come from getAllVisitorsCardsWithoutNestedResolvers.ts, which assigns one each to
//             visitors 8, 9, 11, 12 and 19. Of those, only 11 and 19 are in the overview table,
//             so `hasCard` matches exactly those two there.

const registeredAccessDevice = (id: string) => generateMockMobileDevice({ id, role: 'ACCESS', isRegistered: true });

// --- Installers: the only two INSTALLERS members, so the `installer` role filter returns exactly 2.
export const mockVisitorInstaller = generateMockVisitor({
  id: 'mockVisitorId1',
  name: 'Dennis Tegelwerker',
  email: 'd.tegelwerker@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice1')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.None, visitorGroup: mockVisitorGroupInstallers })
  ]
});

// --- Installer, and the only status-1 visitor: expires this week but not within 24h.
export const mockVisitorInstaller2 = generateMockVisitor({
  id: 'mockVisitorInstaller2',
  name: 'Pieter van den Berg',
  email: 'p.vandenberg@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: false,
  expirationDate: dayjs().add(3, 'days').toISOString(),
  mobileDevices: [registeredAccessDevice('mockedMobileDeviceInstaller2')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.None, visitorGroup: mockVisitorGroupInstallers })
  ]
});

// --- Key manager, ATLAS origin via COGNITO.
export const mockVisitorCleaner = generateMockVisitor({
  id: 'mockVisitorId2',
  name: 'Maarten van der Heijden',
  email: 'm.vanderheijden@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice2')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUserManager,
      visitorGroup: mockVisitorGroupCleaners
    })
  ]
});

// --- Every membership carries an expiration date, so the Expiration column resolves to the LATEST
// of the three (+90 days). Not in the overview table — visitor 5 covers that branch there.
export const mockVisitor3WithGroups = generateMockVisitor({
  id: 'mockVisitorId3',
  name: 'Grzegorz Brzęczyszczykiewicz',
  email: 'g.brzeczyszczykiewicz@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice3')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUser,
      visitorGroup: mockVisitorGroupAlpha,
      expirationDate: dayjs().add(30, 'days').toISOString()
    }),
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUser,
      visitorGroup: mockVisitorGroupBeta,
      expirationDate: dayjs().add(90, 'days').toISOString()
    }),
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUser,
      visitorGroup: mockVisitorGroupGamma,
      expirationDate: dayjs().add(60, 'days').toISOString()
    })
  ]
});

// --- Administrator with a registered ACCESS device and one permanent membership: status 0 and an
// empty Expiration cell. In the overview table it is the plain row the filters should exclude.
export const mockVisitor4 = generateMockVisitor({
  id: 'mockVisitorId4',
  name: 'Hannah Kremer',
  email: 'h.kremer@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: true,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice4')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupDelta })
  ]
});

// --- Carries four things at once, so the 8-row table still covers them: diacritics (Ż/ó) for
// sorting, the key-manager role, status 2 (expires within 24h), and three memberships with
// different dates — the "latest membership wins" branch of the Expiration column (+90 days).
export const mockVisitor5WithGroups = generateMockVisitor({
  id: 'mockVisitorId5',
  name: 'Zofia Żółkiewska',
  email: 'z.zolkiewska@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  expirationDate: dayjs().add(12, 'hours').toISOString(),
  mobileDevices: [registeredAccessDevice('mockedMobileDevice5')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUserManager,
      visitorGroup: mockVisitorGroupEpsilon,
      expirationDate: dayjs().add(30, 'days').toISOString()
    }),
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUser,
      visitorGroup: mockVisitorGroupZeta,
      expirationDate: dayjs().add(90, 'days').toISOString()
    }),
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUser,
      visitorGroup: mockVisitorGroupEta,
      expirationDate: dayjs().add(60, 'days').toISOString()
    })
  ]
});

// --- INTERNAL *with* an email resolves to the ATLAS origin, not Key sharing. Not in the overview
// table — visitor 16 covers that branch there. Uses a non-tkhsecurity.com email domain.
export const mockVisitor6 = generateMockVisitor({
  id: 'mockVisitorId6',
  name: 'Leo de Leeuw',
  email: 'l.deleeuw@ictservices.com',
  identityProvider: UserIdentityProvider.Internal,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice6')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupMu })
  ]
});

// --- 7 groups: the group-chip overflow case. "Omicron Labs" is unique to this visitor, so a search
// for "Omicron" matches on group name alone. Not in the overview table.
export const mockVisitor7WithGroups = generateMockVisitor({
  id: 'mockVisitorId7',
  name: 'Wouter Jansen',
  email: 'w.jansen@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice7')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupIota }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupMu }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupNu }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupXi }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupOmicron }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupEpsilon }),
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupTheta })
  ]
});

// --- Has a card. Membership expires in 1 day, so the Expiration column shows a date while the
// visitor-level expirationDate stays null — the two fields diverge here on purpose.
export const mockVisitor8ExpiringTomorrow = generateMockVisitor({
  id: 'mockVisitorId8',
  name: 'Liesbeth de Hoog',
  email: 'l.dehoog@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice8')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.None,
      visitorGroup: mockVisitorGroupCognitoTeam,
      expirationDate: dayjs().add(1, 'day').toISOString()
    })
  ]
});

// --- Visitor-level expirationDate in the past, so it matches `expired`. Has a card. Not in the
// overview table — visitor 10 is the `expired` match there.
export const mockVisitor9Expired = generateMockVisitor({
  id: 'mockVisitorId9',
  name: 'Martin van Loenen',
  email: 'm.loenen@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Internal,
  isAdmin: false,
  expirationDate: dayjs().subtract(2, 'days').toISOString(),
  mobileDevices: [registeredAccessDevice('mockedMobileDevice9')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.None,
      visitorGroup: mockVisitorGroupInternalTeam,
      expirationDate: dayjs().subtract(1, 'day').toISOString()
    })
  ]
});

// --- Carries three things in the overview table: Key sharing origin (INTERNAL + null email), the
// only `expired` match there, and status 4 via an unregistered ACCESS device with no card.
export const mockVisitor10KeySharing = generateMockVisitor({
  id: 'mockVisitorId10',
  name: 'Mirella van den Broek',
  email: null,
  identityProvider: UserIdentityProvider.Internal,
  isAdmin: false,
  expirationDate: dayjs().subtract(2, 'days').toISOString(),
  mobileDevices: [generateMockMobileDevice({ id: 'mockedMobileDevice10', role: 'ACCESS', isRegistered: false })],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({
      role: Role.ExternalGroupUserManager,
      visitorGroup: mockVisitorGroupKeySharingUsers
    })
  ]
});

// --- Administrator with a card and a registered ACCESS device, so status 0. "Anna Kowalska" pairs
// with "ania kowalczyk" (visitor 19) in the overview table: one edit apart and differently cased,
// which is the Fuse threshold case. Visitor 12 reuses the same name but is not in that table.
export const mockVisitor11WithCard = generateMockVisitor({
  id: 'mockVisitorId11',
  name: 'Anna Kowalska',
  email: 'a.kowalska@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: true,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice11')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupEntraTeam })
  ]
});

// --- Status 3: has a card but no registered ACCESS device. Shares the name "Anna Kowalska" with
// visitor 11. The Atlas Internal membership is hidden from the Groups column but still searchable.
// Not in the overview table.
export const mockVisitor12WithBlockedCard = generateMockVisitor({
  id: 'mockVisitorId12',
  name: 'Anna Kowalska',
  email: 'a.kowalska2@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupCardHolders }),
    generateMockVisitorGroupMembership({ role: Role.None, visitorGroup: mockVisitorGroupKappa })
  ]
});

// --- The two co-contractors back the Groups "Co-contractors" membership and the group detail
// page; neither is in the overview table. 14 keeps diacritics (Ł/ó) for sorting outside it.
export const mockVisitorCoContractor1 = generateMockVisitor({
  id: 'mockVisitorId13',
  name: 'Chen Wei',
  email: 'c.wei@contractor.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice13')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupCoContractors })
  ]
});

export const mockVisitorCoContractor2 = generateMockVisitor({
  id: 'mockVisitorId14',
  name: 'Łukasz Wójcik',
  email: 'l.wojcik@contractor.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice14')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupCoContractors })
  ]
});

// --- No memberships at all: matches `noGroup`, and the Expiration column shows the placeholder.
// Not in the overview table — visitor 16 is the `noGroup` match there, via a hidden membership
// rather than an empty list.
export const mockVisitor15WithoutGroup = generateMockVisitor({
  id: 'mockVisitorId15',
  name: 'Nina Willems',
  email: 'n.willems@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: true,
  mobileDevices: [registeredAccessDevice('mockedMobileDevice15')],
  visitorGroupMembership: []
});

// --- Status 5 (unregistered CONFIG device outranks everything) and the subtle `noGroup` case:
// an Administrators-only membership is hidden, so this visitor counts as having no group despite
// having one. Also the INTERNAL-with-an-email origin, which resolves to ATLAS, not Key sharing.
export const mockVisitor16Disconnected = generateMockVisitor({
  id: 'mockVisitorId16',
  name: 'Bram Visser',
  email: 'b.visser@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Internal,
  isAdmin: false,
  mobileDevices: [generateMockMobileDevice({ id: 'mockedMobileDevice16', role: 'CONFIG', isRegistered: false })],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.None, visitorGroup: mockVisitorGroupLambda })
  ]
});

// --- Status 2, matching `1day` (and `1week`, which spans it). Not in the overview table —
// visitor 5 covers both there.
export const mockVisitor17ExpiringIn24h = generateMockVisitor({
  id: 'mockVisitorId17',
  name: 'Femke Smit',
  email: 'f.smit@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  expirationDate: dayjs().add(12, 'hours').toISOString(),
  mobileDevices: [registeredAccessDevice('mockedMobileDevice17')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupXi })
  ]
});

// --- Status 1: expires this week but not within 24h, so `1week` matches it while `1day` does not.
// Not in the overview table — mockVisitorInstaller2 covers status 1 there.
export const mockVisitor18ExpiringThisWeek = generateMockVisitor({
  id: 'mockVisitorId18',
  name: 'Joris Peeters',
  email: 'j.peeters@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Entra,
  isAdmin: false,
  expirationDate: dayjs().add(3, 'days').toISOString(),
  mobileDevices: [registeredAccessDevice('mockedMobileDevice18')],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupIota })
  ]
});

// --- Status 3: holds the "Loading Dock Badge" (getAllVisitorsCardsWithoutNestedResolvers.ts) but
// has no registered ACCESS device. The card is what separates 3 from 4 — unassign it and this
// visitor drops to status 4. Lowercase first name, one edit away from "Kowalska", which is the
// fuzzy-search threshold case.
export const mockVisitor19DisconnectedWithCard = generateMockVisitor({
  id: 'mockVisitorId19',
  name: 'ania kowalczyk',
  email: 'a.kowalczyk@tkhsecurity.com',
  identityProvider: UserIdentityProvider.Cognito,
  isAdmin: false,
  mobileDevices: [generateMockMobileDevice({ id: 'mockedMobileDevice19', role: 'ACCESS', isRegistered: false })],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.ExternalGroupUser, visitorGroup: mockVisitorGroupZeta })
  ]
});

// --- Status 4 via no devices at all, plus Key sharing origin (INTERNAL + null email) and the
// hidden-membership `noGroup` case. Not in the overview table — 10 and 16 cover those there.
export const mockVisitor20Inactive = generateMockVisitor({
  id: 'mockVisitorId20',
  name: 'Ruben Maes',
  email: null,
  identityProvider: UserIdentityProvider.Internal,
  isAdmin: false,
  mobileDevices: [],
  visitorGroupMembership: [
    generateMockVisitorGroupMembership({ role: Role.None, visitorGroup: mockVisitorGroupLambda })
  ]
});

// Served by GET_VISITORS_WITHOUT_NESTED_RESOLVERS, which is what the User groups tab resolves its
// `memberIds` against. A group whose memberIds point outside this list shows 0 users — silently.
export const mockVisitorsInstallersAndCleaners = [
  mockVisitorInstaller,
  mockVisitorInstaller2,
  mockVisitorCleaner,
  mockVisitorCoContractor1,
  mockVisitorCoContractor2,
  mockVisitor8ExpiringTomorrow,
  mockVisitor9Expired,
  mockVisitor10KeySharing,
  mockVisitor11WithCard,
  mockVisitor12WithBlockedCard
];

export const mockVisitor1 = mockVisitorInstaller;

export const mockVisitor2 = mockVisitorCleaner;

// The Users overview table renders exactly this list — deliberately kept to 8 rows so the whole
// table fits on one screen and "exactly N results" assertions stay readable. The visitors left out
// of it are still exported above and still back their detail pages, cards, access events and the
// dashboard; they are simply not part of the overview fixture.
//
// Keep it in sync with the warm-up query in getAllVisitorsWithoutNestedResolvers.ts — a visitor
// missing there silently vanishes from the table.
//
// Verified distribution (8 visitors) — every sortable column, filter key and search field is
// covered at least once. Measured through the production helpers, not assumed:
//   origin   — Entra ID 4 (1, Installer2, 11, 4) · ATLAS 3 (5, 19, 16) · Key sharing 1 (10).
//              16 is INTERNAL *with* an email, which resolves to ATLAS, not Key sharing.
//   status   — 0: three (1, 11, 4) · 1: Installer2 · 2: 5 · 3: 19 · 4: 10 · 5: 16.
//              All six levels occupied, each by a different visitor.
//   roles    — installer 2 (1, Installer2) · administrator 2 (11, 4) · key manager 2 (5, 10)
//   cards    — 2 (11, 19), so `noCard` matches the other 6
//   noGroup  — 1 (16 has only a hidden Administrators membership)
//   expires  — expired 1 (10) · 1day 1 (5) · 1week 2 (5, Installer2)
//   names    — "Anna Kowalska" / "ania kowalczyk" (one edit apart, mixed case) for the Fuse
//              threshold · "Zofia Żółkiewska" for diacritics · 10 has a null email
//   groups   — chip counts run 0 (16, hidden membership only) · 1 (1, Installer2, 10, 11, 19, 4)
//              · 3 (5). Expiration is the thin spot: 5 is the only visitor whose memberships all
//              carry a date, so it owns the only non-empty cell (+90 days, the latest of its
//              three) and every other row shows the placeholder. That covers null handling but
//              not date ordering — a second dated visitor would be needed to sort the column.
export const mockVisitorsWithGroups = [
  mockVisitor1,
  mockVisitorInstaller2,
  mockVisitor5WithGroups,
  mockVisitor10KeySharing,
  mockVisitor11WithCard,
  mockVisitor16Disconnected,
  mockVisitor19DisconnectedWithCard,
  mockVisitor4
];
